"use client";

import {
  AppFilterController,
  OwnerFilterController,
  type FilterControllerProps,
  type FilterDefinition,
  type LegacyMigrationResult,
  type QuickFilterContext,
  type QuickFilterDefinition,
  useRouterParams,
  useEmitConnectionSearchInteractionEvent,
  ConnectionSearchInteractionOption,
} from "@zapier/shared";
import {
  useConnectionActionPermissions,
  useCurrentUser,
  useSplitTreatments,
} from "@zapier/client";
import { ConnectionStatusFilterController } from "./controllers/ConnectionStatusFilterController";
import { ConnectionSharingFilterController } from "./controllers/ConnectionSharingFilterController";

// Lifted to module scope so React sees a stable component type across
// re-renders — inline arrows would remount the controller each render and
// wipe its internal state (chip flashes empty for one render). Connections'
// `owner` param only accepts customuser IDs, not teams, so this pins
// `allowTeams={false}` on the shared controller. A generic `FilterControllerProps`
// extensibility mechanism is deferred as follow-up polish since only this one
// prop needs pinning today.
const ConnectionsOwnerController = (props: FilterControllerProps) => (
  <OwnerFilterController {...props} allowTeams={false} />
);

// Same reasoning as `ConnectionsOwnerController` above. An authentication has
// exactly one `selectedApi`, so "is all of" across 2+ apps is indistinguishable
// from "is any of" — pinning `availableOperators={["any"]}` hides that redundant
// picker option.
const ConnectionsAppController = (props: FilterControllerProps) => (
  <AppFilterController {...props} availableOperators={["any"]} />
);

export interface ConnectionsFilterConfig {
  filters: FilterDefinition[];
  quickFilters: QuickFilterDefinition[];
  quickFilterContext: QuickFilterContext;
  onFilterInteraction: (type: string) => void;
  legacyMigrate: (
    parameters: ReturnType<typeof useRouterParams>,
  ) => LegacyMigrationResult;
  search: string | undefined;
}

// Connections filter-bar config. Mirrors `useZapFilterConfig`.
export function useConnectionsFilterConfig(): ConnectionsFilterConfig {
  const { user } = useCurrentUser();
  const { canFilterByOwnerStatus } = useConnectionActionPermissions({});
  const { isManagedConnectionsFilterEnabled } = useSplitTreatments();
  const { owners, search } = useRouterParams();
  const { emit } = useEmitConnectionSearchInteractionEvent();

  const filters: FilterDefinition[] = [
    {
      type: "status",
      icon: "statusStatus",
      labelKey: "status.label",
      paramNames: ["status"],
      Controller: ConnectionStatusFilterController,
    },
    {
      type: "owner",
      icon: "personCircle",
      labelKey: "owner.label",
      paramNames: ["owners", "ownersOperator"],
      Controller: ConnectionsOwnerController,
    },
    {
      type: "app",
      icon: "navApps",
      labelKey: "app.label",
      paramNames: ["apps", "appsOperator"],
      Controller: ConnectionsAppController,
    },
    {
      type: "sharing",
      icon: "personGroup",
      labelKey: "sharing.label",
      paramNames: ["shared", "ownerStatus"],
      Controller: ConnectionSharingFilterController,
    },
  ];

  // Quick filters that write `shared` overwrite each other naturally and
  // clear `ownerStatus`. "Owned by me" instead writes `owners=<me>` and
  // clears the access params (per design — it's an owner selection).
  const quickFilters: QuickFilterDefinition[] = [
    {
      key: "ownedByMe",
      icon: "personOne",
      labelKey: "quickFilters.ownedByMe",
      isDisabled: (context) => !context.isUserResolved,
      apply: (setParams) => {
        if (!user) return;
        emit(ConnectionSearchInteractionOption.OwnedByMe);
        setParams({ owners: String(user.id), page: 1 }, [
          "owner",
          "ownersOperator",
          "shared",
          "ownerStatus",
        ]);
      },
    },
    {
      key: "sharedWithMe",
      icon: "personGroup",
      labelKey: "quickFilters.sharedWithMe",
      apply: (setParams) => {
        emit(ConnectionSearchInteractionOption.SharedWithMe);
        // "Shared with me" + "owned by me" is empty by definition, so drop
        // the owner param if it points to the current user. Any other owner
        // selection is preserved.
        const ownerIsMe = !!owners && !!user && owners === String(user.id);
        setParams(
          { shared: "-me", page: 1 },
          ownerIsMe
            ? ["owners", "ownersOperator", "ownerStatus"]
            : ["ownerStatus"],
        );
      },
    },
    {
      key: "sharedByMe",
      icon: "personGroup",
      labelKey: "quickFilters.sharedByMe",
      apply: (setParams) => {
        emit(ConnectionSearchInteractionOption.SharedByMe);
        setParams({ shared: "shared-by-me", page: 1 }, ["ownerStatus"]);
      },
    },
    {
      key: "managed",
      icon: "miscSecurity",
      labelKey: "quickFilters.managed",
      isAvailable: (context) => context.isManagedConnectionsFilterEnabled,
      apply: (setParams) => {
        emit(ConnectionSearchInteractionOption.Managed);
        setParams({ shared: "managed", page: 1 }, ["ownerStatus"]);
      },
    },
    // "Inactive members" is admin-gated and lives only in the Access dropdown.
  ];

  const quickFilterContext: QuickFilterContext = {
    isUserResolved: !!user,
    isManagedConnectionsFilterEnabled,
    canFilterByOwnerStatus,
  };

  const onFilterInteraction = (type: string) => {
    // Sharing emits per-option analytics from its own controller.
    const option = {
      status: ConnectionSearchInteractionOption.ConnectionStatus,
      owner: ConnectionSearchInteractionOption.Owner,
      app: ConnectionSearchInteractionOption.AppUsed,
    }[type];
    if (option) emit(option);
  };

  // Rewrites the pre-multi-select `owner`/`app` params (still reachable via
  // old bookmarks and `getConnectionsByAppUrl`) into `owners`/`apps` on mount,
  // so the Owner/App chips and Clear all pick them up. Connections' legacy
  // `owner` is always a bare customuser ID (no `me`/`!me` tokens), unlike Zaps'
  // `legacyMigrate`, so this doesn't need the current user to resolve first.
  const legacyMigrate = (
    parameters: ReturnType<typeof useRouterParams>,
  ): LegacyMigrationResult => {
    const {
      owner: legacyOwner,
      app: legacyApp,
      owners: currentOwners,
      apps: currentApps,
    } = parameters;
    const hasLegacyParameters = !!legacyOwner || !!legacyApp;

    const set: Record<string, string> = {};
    const omit: string[] = [];

    if (legacyOwner) {
      if (!currentOwners) set.owners = legacyOwner;
      omit.push("owner");
    }
    if (legacyApp) {
      // Legacy `app` may carry a versioned/`::name`-suffixed id; `apps` carries
      // bare selectedApi ids (mirrors the stripping in useConnectionsList).
      if (!currentApps) set.apps = legacyApp.split("@")[0].split("::")[0];
      omit.push("app");
    }

    return { set, omit, hasLegacyParameters };
  };

  return {
    filters,
    quickFilters,
    quickFilterContext,
    onFilterInteraction,
    legacyMigrate,
    search: search ?? undefined,
  };
}
